/** 当前账号唯一 Workspace 与有效权限快照。 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { workspaceApi } from '@/api/modules'
import { cacheManager } from '@/utils/request/request-cache'
import { normalizeAuthorizationContext } from '@/utils/auth/authorization'
import type {
  ApplicationEntry,
  AuthorizationContext,
  DataScope,
  EffectivePermissionMap,
  SensitiveFieldPermission,
  LearningExperienceMode,
  WorkspaceAccessState,
} from '@/types/workspace'

type PermissionMode = 'read' | 'write'

const HIERARCHICAL_SCOPE_INCLUDES: Partial<Record<DataScope, DataScope[]>> = {
  classroom: ['classroom'],
  academic_grade: ['academic_grade', 'classroom'],
  organization: ['organization', 'academic_grade', 'classroom'],
  Workspace: ['Workspace', 'organization', 'academic_grade', 'classroom'],
  platform: ['platform', 'Workspace', 'organization', 'academic_grade', 'classroom'],
}

export const useWorkspaceStore = defineStore('workspace', () => {
  /** 当前认证上下文；登出、无成员关系或上下文加载失败时为 null。 */
  const context = ref<AuthorizationContext | null>(null)

  /** 上下文请求状态。 */
  const loading = ref(false)

  /** 当前账号是否已有可访问的唯一 Workspace 成员关系。 */
  const hasWorkspace = computed(
    () => context.value?.access_state === 'active' && context.value.workspace !== null
  )

  /** 后端统一裁决的当前访问状态。 */
  const accessState = computed<WorkspaceAccessState | null>(
    () => context.value?.access_state ?? null
  )

  /** 当前账号是否可访问工作空间业务数据。 */
  const isAccessActive = computed(() => accessState.value === 'active')

  /** 当前工作空间 ID。 */
  const workspaceId = computed(() => context.value?.workspace?.id ?? null)

  /** 当前工作空间名称。 */
  const workspaceName = computed(() => context.value?.workspace?.name ?? '')

  /** 当前 Tenant ID。 */
  const tenantId = computed(() => context.value?.workspace?.tenant_id ?? null)

  /** 默认公司保留 C 端体验，其他租户使用学习门户体验。 */
  const experienceMode = computed<LearningExperienceMode>(() =>
    context.value?.workspace?.is_company === false ? 'workspace' : 'consumer'
  )

  /** 当前是否为外部工作空间学习门户。 */
  const isExternalWorkspace = computed(() => experienceMode.value === 'workspace')

  /** 当前是否为启用学校治理能力的学校工作空间。 */
  const isSchoolWorkspace = computed(() => context.value?.workspace?.tenant_type === 'school')

  /** 当前是否为默认公司 C 端工作空间。 */
  const isCompanyWorkspace = computed(() => experienceMode.value === 'consumer')

  /** 当前角色，仅用于说明授权来源，不参与鉴权判断。 */
  const roles = computed(() => context.value?.roles ?? [])

  /** 权限码到独立读写数据范围的有效快照。 */
  const permissions = computed<EffectivePermissionMap>(() => context.value?.permissions ?? {})

  /** 当前可进入的应用。 */
  const applications = computed<ApplicationEntry[]>(() => context.value?.applications ?? [])

  /** 当前可查看的敏感字段组。 */
  const fieldPermissions = computed<SensitiveFieldPermission[]>(
    () => context.value?.field_permissions ?? []
  )

  /** 判断是否具备应用入口。 */
  const hasApplication = (application: ApplicationEntry): boolean =>
    applications.value.includes(application)

  /** 判断权限点是否存在，不以角色名称或前端菜单作为授权依据。 */
  const hasPermission = (code: string): boolean => code in permissions.value

  /** 判断任一权限点是否存在。 */
  const hasAnyPermission = (codes: readonly string[]): boolean => codes.some(hasPermission)

  /** 判断全部权限点是否存在。 */
  const hasAllPermissions = (codes: readonly string[]): boolean => codes.every(hasPermission)

  /** 获取权限点的读取范围。 */
  const readScopesOf = (code: string): DataScope[] => permissions.value[code]?.read_scopes ?? []

  /** 获取权限点的写入范围。 */
  const writeScopesOf = (code: string): DataScope[] => permissions.value[code]?.write_scopes ?? []

  /**
   * 判断权限是否覆盖指定范围。public/self/assigned 只做精确匹配，组织层级范围按包含关系判断。
   */
  const hasScope = (code: string, required: DataScope, mode: PermissionMode = 'read'): boolean => {
    const scopes = mode === 'read' ? readScopesOf(code) : writeScopesOf(code)

    return scopes.some(scope => {
      if (scope === required) return true
      return HIERARCHICAL_SCOPE_INCLUDES[scope]?.includes(required) ?? false
    })
  }

  /** 判断是否可查看敏感字段组。本人完整资料由具体页面额外处理。 */
  const hasFieldPermission = (permission: SensitiveFieldPermission): boolean =>
    fieldPermissions.value.includes(permission)

  /** 写入并规范化上下文。 */
  const setContext = (next: unknown): AuthorizationContext | null => {
    const normalized = normalizeAuthorizationContext(next)
    const identityChanged =
      context.value?.user.id !== normalized?.user.id ||
      context.value?.workspace?.id !== normalized?.workspace?.id
    if (identityChanged) cacheManager.clear()
    context.value = normalized
    return context.value
  }

  /** 从统一认证接口刷新用户、Workspace、角色和权限。 */
  const fetchContext = async (): Promise<AuthorizationContext | null> => {
    loading.value = true
    try {
      const response = await workspaceApi.getAuthorizationContext()
      return setContext(response.data)
    } catch {
      context.value = null
      return null
    } finally {
      loading.value = false
    }
  }

  /** 根据有效应用入口返回当前账号在 Frontend 的默认页面。 */
  const defaultEntryPath = (): string => {
    if (!isAccessActive.value) return '/workspace/status'
    if (hasApplication('frontend')) return '/learning'
    return '/access-denied'
  }

  /** 清除权限上下文。 */
  const clearContext = () => {
    cacheManager.clear()
    context.value = null
  }

  return {
    context,
    loading,
    hasWorkspace,
    accessState,
    isAccessActive,
    workspaceId,
    workspaceName,
    tenantId,
    experienceMode,
    isExternalWorkspace,
    isSchoolWorkspace,
    isCompanyWorkspace,
    roles,
    permissions,
    applications,
    fieldPermissions,
    hasApplication,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    readScopesOf,
    writeScopesOf,
    hasScope,
    hasFieldPermission,
    setContext,
    fetchContext,
    defaultEntryPath,
    clearContext,
  }
})
